import { DoneAll as DoneAllIcon } from '@mui/icons-material';
import { Checkbox, CircularProgress } from '@mui/material';
import { useMemo, useState } from 'react';

import { VNFieldHelpItemTipIcon } from '@/components/VNFieldHelp';

import type { DropdownOption, FilterOption, ListSlotProps } from '../types';

// ─── FilterBar ───────────────────────────────────────────────────────────────

const ALL_KEY = '__all__';

interface FilterBarProps {
  filterOptions: FilterOption[];
  activeFilterKey: string | number;
  onChange: (key: string | number) => void;
  multiple: boolean;
  onSelectAll?: (opts: DropdownOption[]) => void;
  visibleOptions: DropdownOption[];
  allVisibleSelected: boolean;
}

const FilterBar = ({
  filterOptions,
  activeFilterKey,
  onChange,
  multiple,
  onSelectAll,
  visibleOptions,
  allVisibleSelected,
}: FilterBarProps) => {
  const tabs = useMemo(
    () => [{ key: ALL_KEY as string | number, label: 'All' }, ...filterOptions.map((fo) => ({ key: fo.key, label: fo.label }))],
    [filterOptions]
  );
  const activeIndex = tabs.findIndex((t) => t.key === activeFilterKey);
  const tabCount = tabs.length;

  return (
    <div className="vn-dropdown-list__filter-bar">
      <div className="vn-dropdown-list__filter-box">
        {/* Sliding indicator — pure CSS transform, no DOM measurement */}
        <span
          className="vn-dropdown-list__filter-indicator"
          aria-hidden="true"
          style={{
            width: `calc((100% - 4px) / ${tabCount})`,
            transform: `translateX(${activeIndex * 100}%)`,
          }}
        />
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={['vn-dropdown-list__filter-btn', tab.key === activeFilterKey ? 'vn-dropdown-list__filter-btn--active' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onChange(tab.key)}
            onMouseDown={(e) => e.preventDefault()}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {multiple && onSelectAll && (
        <button
          type="button"
          className={[
            'vn-dropdown-list__filter-select-all',
            allVisibleSelected ? 'vn-dropdown-list__filter-select-all--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => onSelectAll(visibleOptions)}
          onMouseDown={(e) => e.preventDefault()}
          aria-label={allVisibleSelected ? 'Deselect all' : 'Select all'}
          title={allVisibleSelected ? 'Deselect all' : 'Select all'}
        >
          <DoneAllIcon fontSize="small" />
        </button>
      )}
    </div>
  );
};

// ─── VNDropdownList ───────────────────────────────────────────────────────────

const VNDropdownList = ({
  options,
  selectedIds,
  onSelect,
  onSelectAll,
  multiple = false,
  focusedIndex,
  listboxId,
  optionIdPrefix,
  filterOptions,
  isLiveLoading = false,
  dropDownItemHelperTooltip,
  dropDownItemHelperText,
}: ListSlotProps) => {
  const hasItemHelp = Boolean(
    (dropDownItemHelperTooltip && Object.keys(dropDownItemHelperTooltip).length > 0) ||
      (dropDownItemHelperText && Object.keys(dropDownItemHelperText).length > 0)
  );
  const [activeFilterKey, setActiveFilterKey] = useState<string | number>(ALL_KEY);

  const visibleOptions =
    activeFilterKey !== ALL_KEY && filterOptions
      ? (() => {
          const fo = filterOptions.find((f) => f.key === activeFilterKey);
          return fo ? options.filter((o) => o[fo.propertyPath] === fo.key) : options;
        })()
      : options;

  const selectableVisibleOptions = visibleOptions.filter((o) => !o.disabled);
  const allVisibleSelected =
    selectableVisibleOptions.length > 0 && selectableVisibleOptions.every((o) => selectedIds.includes(o.id));

  return (
    <div>
      {filterOptions && filterOptions.length > 0 && (
        <FilterBar
          filterOptions={filterOptions}
          activeFilterKey={activeFilterKey}
          onChange={setActiveFilterKey}
          multiple={multiple}
          onSelectAll={onSelectAll}
          visibleOptions={selectableVisibleOptions}
          allVisibleSelected={allVisibleSelected}
        />
      )}
      <ul id={listboxId} role="listbox" aria-multiselectable={multiple || undefined} className="vn-dropdown-list">
        {visibleOptions.length === 0 && (
          <li role="option" aria-selected={false} aria-disabled="true" className="vn-dropdown-list__empty">
            No options available
          </li>
        )}

        {visibleOptions.map((option: DropdownOption, index: number) => {
          const isSelected = selectedIds.includes(option.id);
          const isFocused = index === focusedIndex;
          const isDisabled = Boolean(option.disabled);
          const key = String(option.id);
          const itemTooltip = dropDownItemHelperTooltip?.[key];
          const itemHelperText = dropDownItemHelperText?.[key];

          return (
            <li
              key={option.id}
              id={`${optionIdPrefix}-${index}`}
              role="option"
              aria-selected={isSelected}
              aria-disabled={isDisabled || undefined}
              className={[
                'vn-dropdown-list__option',
                hasItemHelp ? 'vn-dropdown-list__option--with-help vn-field-help-menu-item' : '',
                isSelected ? 'vn-dropdown-list__option--selected' : '',
                isFocused ? 'vn-dropdown-list__option--focused' : '',
                isDisabled ? 'vn-dropdown-list__option--disabled' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={(e) => {
                if (isDisabled) return;
                if ((e.target as Element).closest('.vn-field-help-menu-item__icon-wrap')) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                onSelect(option);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (isDisabled) return;
                  onSelect(option);
                }
              }}
              onMouseDown={(e) => e.preventDefault()}
            >
              {multiple && (
                <Checkbox
                  checked={isSelected}
                  className="vn-dropdown-list__checkbox"
                  aria-hidden="true"
                  tabIndex={-1}
                  size="small"
                  disableRipple
                  onMouseDown={(e) => e.preventDefault()}
                  style={{
                    transform: 'scale(.6)',
                    marginRight: 8,
                    marginBottom: 3,
                  }}
                />
              )}
              {hasItemHelp ? (
                <div className="vn-field-help-menu-item__content">
                  <div className="vn-field-help-menu-item__main">
                    <span className="vn-field-help-menu-item__label">{option.label}</span>
                    {itemTooltip && <VNFieldHelpItemTipIcon tooltip={itemTooltip} />}
                  </div>
                  {itemHelperText && <span className="vn-field-help-menu-item__subtext">{itemHelperText}</span>}
                </div>
              ) : (
                <>
                  {option.icon && option.iconAlign !== 'right' && (
                    <span className="vn-dropdown-list__icon vn-dropdown-list__icon--left">{option.icon}</span>
                  )}
                  <span className="vn-dropdown-list__label">{option.label}</span>
                  {option.icon && option.iconAlign === 'right' && (
                    <span className="vn-dropdown-list__icon vn-dropdown-list__icon--right">{option.icon}</span>
                  )}
                </>
              )}
            </li>
          );
        })}
        {isLiveLoading && (
          <li role="presentation" aria-hidden="true" className="vn-dropdown-list__spinner">
            <CircularProgress size={18} color="inherit" />
          </li>
        )}
      </ul>
    </div>
  );
};

export default VNDropdownList;
